import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table, Button, Modal, Form, Input, Select, InputNumber,
  Switch, Tag, Space, Popconfirm, Tooltip, DatePicker, Badge, Spin,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  SettingOutlined, SaveOutlined, UserOutlined, ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  fetchCoupons, createCoupon, updateCoupon, deleteCoupon,
  toggleCouponStatus, setFilters,
  fetchGlobalSettings, updateGlobalSettings,
} from "../../../core/data/redux/slices/CouponSlice";

const { Option } = Select;

export default function AdminCoupons() {
  const dispatch = useDispatch();
  const {
    coupons, total, totalPages, loading, actionLoading, filters,
    globalSettings, settingsLoading, settingsSaving,
  } = useSelector((state) => state.coupons);

  const [form] = Form.useForm();
  const [couponModal, setCouponModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [usageModal, setUsageModal] = useState(false);
  const [usageCoupon, setUsageCoupon] = useState(null); // coupon whose usage list is shown

  // Retention settings local edit state
  const [retentionPercent, setRetentionPercent] = useState(globalSettings?.retentionDiscountPercent ?? 30);
  useEffect(() => {
    if (globalSettings?.retentionDiscountPercent !== undefined) {
      setRetentionPercent(globalSettings.retentionDiscountPercent);
    }
  }, [globalSettings?.retentionDiscountPercent]);

  // Load coupons on mount and when filters change
  useEffect(() => {
    const { page, limit, discountType, status, search, isRetentionCoupon } = filters;
    dispatch(fetchCoupons({
      page, limit,
      discountType: discountType || undefined,
      status: status || undefined,
      search: search || undefined,
      isRetentionCoupon: isRetentionCoupon || undefined,
    }));
  }, [dispatch, filters]);

  // Load global settings on mount
  useEffect(() => {
    dispatch(fetchGlobalSettings());
  }, [dispatch]);

  const openCreate = () => {
    setEditingCoupon(null);
    form.resetFields();
    setCouponModal(true);
  };

  const openEdit = (coupon) => {
    setEditingCoupon(coupon);
    form.setFieldsValue({
      name: coupon.name,
      expiresAt: coupon.expiresAt ? dayjs(coupon.expiresAt) : null,
      isActive: coupon.isActive,
    });
    setCouponModal(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        expiresAt: values.expiresAt ? values.expiresAt.toISOString() : null,
      };
      if (editingCoupon) {
        await dispatch(updateCoupon({ couponId: editingCoupon._id, ...payload })).unwrap();
      } else {
        await dispatch(createCoupon(payload)).unwrap();
      }
      setCouponModal(false);
    } catch (_) {}
  };

  const searchDebounceRef = useRef(null);

  const handleFilterChange = (key, value) => {
    if (key === "search") {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = setTimeout(() => {
        dispatch(setFilters({ search: value, page: 1 }));
      }, 300);
    } else {
      dispatch(setFilters({ [key]: value, page: 1 }));
    }
  };

  const isExpired = (coupon) => coupon.expiresAt && new Date(coupon.expiresAt) < new Date();

  const formatUserName = (user) => {
    if (!user) return "—";
    const name = [user.firstname, user.lastname].filter(Boolean).join(" ");
    return name || user.email || "—";
  };

  const columns = [
    {
      title: "Name / Code",
      dataIndex: "name",
      render: (name, record) => (
        <Space direction="vertical" size={2}>
          <Space size={4}>
            <span style={{ fontWeight: 600 }}>{name}</span>
            {record.isRetentionCoupon && (
              <Tag color="purple" style={{ fontSize: 10, padding: "0 6px" }}>Retention</Tag>
            )}
          </Space>
          <Tag style={{ letterSpacing: 1 }}>{record.code}</Tag>
        </Space>
      ),
    },
    {
      title: "Discount",
      render: (_, record) => (
        <span style={{ fontWeight: 600, color: "#6366f1" }}>
          {record.discountType === "percentage"
            ? `${record.discountValue}% off`
            : `$${record.discountValue} off`}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "discountType",
      render: (t) => (
        <Tag color={t === "percentage" ? "purple" : "blue"}>
          {t === "percentage" ? "Percentage" : "Fixed ($)"}
        </Tag>
      ),
    },
    {
      title: "Generated For",
      dataIndex: "generatedForUserId",
      render: (user) => {
        if (!user) return <span style={{ color: "#d1d5db" }}>—</span>;
        return (
          <Space direction="vertical" size={0}>
            <span style={{ fontWeight: 500, fontSize: 13 }}>
              <UserOutlined style={{ marginRight: 4, color: "#6366f1" }} />
              {formatUserName(user)}
            </span>
            {user.email && <span style={{ color: "#9ca3af", fontSize: 12 }}>{user.email}</span>}
          </Space>
        );
      },
    },
    {
      title: "Used",
      render: (_, record) => {
        const count = record.timesUsed || 0;
        const max = record.maxUses;
        return (
          <Space direction="vertical" size={2}>
            <Tag color={count === 0 ? "default" : "purple"} style={{ fontWeight: 600, fontSize: 13 }}>
              {count}{max ? ` / ${max}` : ""}
            </Tag>
            {count > 0 && (
              <Button
                type="link"
                size="small"
                style={{ padding: 0, fontSize: 12, height: "auto" }}
                icon={<ClockCircleOutlined />}
                onClick={() => { setUsageCoupon(record); setUsageModal(true); }}
              >
                View users
              </Button>
            )}
          </Space>
        );
      },
    },
    {
      title: "Expiry",
      dataIndex: "expiresAt",
      render: (d) => {
        if (!d) return <Tag>Never expires</Tag>;
        const expired = new Date(d) < new Date();
        return (
          <Tag color={expired ? "red" : "green"}>
            {expired ? "Expired · " : ""}{dayjs(d).format("MMM D, YYYY")}
          </Tag>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (active, record) => (
        <Switch
          checked={active && !isExpired(record)}
          size="small"
          onChange={() => dispatch(toggleCouponStatus(record._id))}
          disabled={isExpired(record)}
        />
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Popconfirm
            title="Delete this coupon?"
            onConfirm={() => dispatch(deleteCoupon(record._id))}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <h3 style={{ margin: 0, fontWeight: 700 }}>Coupons Management</h3>
            <span style={{ color: "#6b7280" }}>Create and manage discount coupons</span>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ background: "#6366f1" }}>
            Add Coupon
          </Button>
        </div>

        {/* ── Retention Offer Settings ─────────────────────────────────────── */}
        <div style={{
          background: "#fff",
          borderRadius: 12,
          padding: "20px 24px",
          marginBottom: 20,
          boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
          borderLeft: "4px solid #6366f1",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <SettingOutlined style={{ color: "#6366f1" }} />
                <span style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>Retention Offer Settings</span>
              </div>
              <p style={{ margin: 0, color: "#6b7280", fontSize: 13 }}>
                Set the discount % shown to subscribers who try to cancel. A unique coupon is generated per user when they click "Avail Offer".
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {settingsLoading ? (
                <Spin size="small" />
              ) : (
                <>
                  <span style={{ fontWeight: 500, fontSize: 13, color: "#374151" }}>Discount:</span>
                  <InputNumber
                    min={1}
                    max={100}
                    value={retentionPercent}
                    onChange={(v) => setRetentionPercent(v)}
                    formatter={(v) => `${v}%`}
                    parser={(v) => v.replace("%", "")}
                    style={{ width: 90 }}
                  />
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={settingsSaving}
                    style={{ background: "#6366f1" }}
                    onClick={() => dispatch(updateGlobalSettings({ retentionDiscountPercent: retentionPercent }))}
                  >
                    Save
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div style={{ background: "#fff", borderRadius: 12, padding: 16, marginBottom: 16, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search by name or code..."
            style={{ width: 240 }}
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
            allowClear
          />
          <Select
            placeholder="Discount Type"
            style={{ width: 160 }}
            value={filters.discountType || undefined}
            onChange={(v) => handleFilterChange("discountType", v || "")}
            allowClear
          >
            <Option value="percentage">Percentage</Option>
            <Option value="fixed">Fixed ($)</Option>
          </Select>
          <Select
            placeholder="Status"
            style={{ width: 140 }}
            value={filters.status || undefined}
            onChange={(v) => handleFilterChange("status", v || "")}
            allowClear
          >
            <Option value="active">Active</Option>
            <Option value="inactive">Inactive</Option>
            <Option value="expired">Expired</Option>
          </Select>
          <Select
            placeholder="Coupon Type"
            style={{ width: 160 }}
            value={filters.isRetentionCoupon || undefined}
            onChange={(v) => handleFilterChange("isRetentionCoupon", v || "")}
            allowClear
          >
            <Option value="true">Retention Only</Option>
            <Option value="false">Standard Only</Option>
          </Select>
          <Badge count={total} style={{ backgroundColor: "#6366f1" }}>
            <span style={{ padding: "0 8px", lineHeight: "30px", color: "#6b7280", fontSize: 13 }}>{total} total</span>
          </Badge>
        </div>

        {/* Table */}
        <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 1px 8px rgba(0,0,0,0.06)" }}>
          <Table
            columns={columns}
            dataSource={coupons}
            rowKey="_id"
            loading={loading}
            scroll={{ x: 900 }}
            pagination={{
              total,
              pageSize: filters.limit,
              current: filters.page,
              onChange: (page) => handleFilterChange("page", page),
              showTotal: (t) => `${t} coupons`,
            }}
          />
        </div>

        {/* ── Usage History Modal ──────────────────────────────────────────── */}
        <Modal
          title={
            usageCoupon ? (
              <Space direction="vertical" size={2}>
                <span style={{ fontWeight: 700, fontSize: 16 }}>
                  Coupon Usage — {usageCoupon.name}
                </span>
                <Tag style={{ letterSpacing: 1, fontWeight: 600 }}>{usageCoupon.code}</Tag>
              </Space>
            ) : "Coupon Usage"
          }
          open={usageModal}
          onCancel={() => { setUsageModal(false); setUsageCoupon(null); }}
          footer={null}
          width={620}
        >
          {usageCoupon && (
            <>
              <div style={{ display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" }}>
                <Tag color="purple" style={{ fontWeight: 600, fontSize: 13 }}>
                  {usageCoupon.timesUsed || 0}{usageCoupon.maxUses ? ` / ${usageCoupon.maxUses}` : ""} uses
                </Tag>
                {usageCoupon.isRetentionCoupon && <Tag color="purple">Retention Coupon</Tag>}
                <Tag color={usageCoupon.discountType === "percentage" ? "purple" : "blue"}>
                  {usageCoupon.discountType === "percentage"
                    ? `${usageCoupon.discountValue}% off`
                    : `$${usageCoupon.discountValue} off`}
                </Tag>
              </div>
              {(!usageCoupon.usageHistory || usageCoupon.usageHistory.length === 0) ? (
                <div style={{ textAlign: "center", padding: "32px 0", color: "#9ca3af" }}>
                  No usage records yet.
                </div>
              ) : (
                <Table
                  dataSource={usageCoupon.usageHistory}
                  rowKey={(r, i) => r.invoiceId || i}
                  size="small"
                  pagination={usageCoupon.usageHistory.length > 8 ? { pageSize: 8 } : false}
                  columns={[
                    {
                      title: "User",
                      render: (_, r) => {
                        const user = r.userId;
                        if (!user) return <span style={{ color: "#d1d5db" }}>—</span>;
                        const name = [user.firstname, user.lastname].filter(Boolean).join(" ");
                        return (
                          <Space direction="vertical" size={0}>
                            <span style={{ fontWeight: 500, fontSize: 13 }}>
                              <UserOutlined style={{ marginRight: 4, color: "#6366f1" }} />
                              {name || "—"}
                            </span>
                            {user.email && (
                              <span style={{ color: "#9ca3af", fontSize: 12 }}>{user.email}</span>
                            )}
                          </Space>
                        );
                      },
                    },
                    {
                      title: "Used At",
                      dataIndex: "usedAt",
                      render: (d) => d
                        ? <Tag color="blue">{dayjs(d).format("MMM D, YYYY · h:mm A")}</Tag>
                        : <span style={{ color: "#d1d5db" }}>—</span>,
                    },
                    {
                      title: "Invoice",
                      dataIndex: "invoiceId",
                      render: (id) => id
                        ? <Tooltip title={id}><Tag style={{ fontFamily: "monospace", fontSize: 11 }}>{id.slice(0, 18)}…</Tag></Tooltip>
                        : <span style={{ color: "#d1d5db" }}>—</span>,
                    },
                  ]}
                />
              )}
            </>
          )}
        </Modal>

        {/* ── Add / Edit Coupon Modal ───────────────────────────────────────── */}
        <Modal
          title={editingCoupon ? "Edit Coupon" : "Create New Coupon"}
          open={couponModal}
          onCancel={() => setCouponModal(false)}
          onOk={handleSave}
          confirmLoading={actionLoading}
          okText="Save Coupon"
          okButtonProps={{ style: { background: "#6366f1" } }}
          width={520}
        >
          <Form form={form} layout="vertical">
            <Form.Item label="Coupon Name" name="name" rules={[{ required: true, message: "Name is required" }]}>
              <Input placeholder="e.g. Summer Sale 2024" />
            </Form.Item>

            {!editingCoupon && (
              <>
                <Form.Item
                  label="Coupon Code"
                  name="code"
                  rules={[{ required: true, message: "Code is required" }]}
                  extra="Code will be auto-uppercased"
                >
                  <Input placeholder="e.g. SUMMER20" style={{ textTransform: "uppercase" }} />
                </Form.Item>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <Form.Item label="Discount Type" name="discountType" rules={[{ required: true }]}>
                    <Select placeholder="Select type">
                      <Option value="percentage">Percentage (%)</Option>
                      <Option value="fixed">Fixed Amount ($)</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item label="Discount Value" name="discountValue" rules={[{ required: true, message: "Value required" }]}>
                    <InputNumber min={0} style={{ width: "100%" }} placeholder="e.g. 20" />
                  </Form.Item>
                </div>
              </>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <Form.Item label="Expiry Date" name="expiresAt" extra="Leave empty for no expiry">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item label="Active" name="isActive" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </div>

            {editingCoupon && (
              <div style={{ background: "#fef3c7", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#92400e" }}>
                Note: Code and discount values cannot be changed after creation (Stripe limitation).
              </div>
            )}
          </Form>
        </Modal>
      </div>
    </div>
  );
}
